// frontend/src/components/platform/PlatformTrustScore.jsx
// 6.C2 - UniLedge Platform Trust Score - PUBLIC display component
// Shows UniLedge's own accountability score - "We follow our own rules"

import React from 'react';
import { useQuery, gql } from '@apollo/client';
import { Shield, TrendingUp, Users, CheckCircle, Clock, Star, AlertCircle } from 'lucide-react';

// Query for platform accountability metrics
const GET_PLATFORM_ACCOUNTABILITY = gql`
  query PlatformAccountability {
    platformAccountability {
      platformTrustScore
      uptime
      activeUsersThisWeek
      activeUsersThisMonth
      jobCompletionRate
      feedbackByCategory {
        category
        count
        avgRating
      }
    }
  }
`;

// Size variants for different contexts
const sizeConfig = {
  small: {
    container: 'p-3',
    icon: 16,
    score: 'text-2xl',
    label: 'text-xs',
    details: false
  },
  medium: {
    container: 'p-4',
    icon: 20,
    score: 'text-3xl',
    label: 'text-sm',
    details: true
  },
  large: {
    container: 'p-6',
    icon: 24,
    score: 'text-4xl',
    label: 'text-base',
    details: true
  },
  hero: {
    container: 'p-8',
    icon: 32,
    score: 'text-5xl',
    label: 'text-lg',
    details: true
  }
};

// Get color based on score
const getScoreColor = (score) => {
  if (score >= 4.5) return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', fill: 'fill-green-500' };
  if (score >= 4.0) return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', fill: 'fill-blue-500' };
  if (score >= 3.5) return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-600', fill: 'fill-yellow-500' };
  if (score >= 3.0) return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600', fill: 'fill-orange-500' };
  return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', fill: 'fill-red-500' };
};

// Star rating display
const StarRating = ({ score, size = 16 }) => {
  const fullStars = Math.floor(score);
  const hasHalf = score % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
  
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(fullStars)].map((_, i) => (
        <Star key={`full-${i}`} size={size} className="text-yellow-400 fill-yellow-400" />
      ))}
      {hasHalf && (
        <div className="relative">
          <Star size={size} className="text-gray-300" />
          <div className="absolute inset-0 overflow-hidden w-1/2">
            <Star size={size} className="text-yellow-400 fill-yellow-400" />
          </div>
        </div>
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <Star key={`empty-${i}`} size={size} className="text-gray-300" />
      ))}
    </div>
  );
};

// Main component
const PlatformTrustScore = ({ 
  size = 'medium', 
  showDetails = true,
  showCommitment = false,
  className = '' 
}) => {
  const { data, loading, error } = useQuery(GET_PLATFORM_ACCOUNTABILITY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 300000 // Refresh every 5 minutes
  });

  const config = sizeConfig[size] || sizeConfig.medium;
  const metrics = data?.platformAccountability;
  const score = metrics?.platformTrustScore || 0;
  const colors = getScoreColor(score);

  if (loading && !data) {
    return (
      <div className={`${colors.bg} ${colors.border} border rounded-lg ${config.container} ${className} animate-pulse`}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gray-200 rounded-full" />
          <div className="space-y-2">
            <div className="h-6 w-20 bg-gray-200 rounded" />
            <div className="h-4 w-32 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg ${config.container} ${className}`}>
        <div className="flex items-center gap-2 text-gray-500">
          <AlertCircle size={config.icon} />
          <span className={config.label}>Trust score unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`${colors.bg} ${colors.border} border rounded-lg ${config.container} ${className}`}>
      {/* Main Score Display */}
      <div className="flex items-center gap-4">
        <div className={`${colors.bg} p-3 rounded-full border-2 ${colors.border}`}>
          <Shield size={config.icon} className={colors.text} />
        </div>
        
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <span className={`${config.score} font-bold ${colors.text}`}>
              {score.toFixed(1)}
            </span>
            <span className="text-gray-500">/5.0</span>
          </div>
          
          <div className="flex items-center gap-2 mt-1">
            <StarRating score={score} size={config.icon * 0.75} />
            <span className={`${config.label} text-gray-600`}>
              UniLedge Platform Score
            </span>
          </div>
        </div>
      </div>

      {/* Details Section */}
      {showDetails && config.details && metrics && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            {/* Uptime */}
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Uptime</p>
                <p className="font-semibold text-gray-900">
                  {(metrics.uptime * 100).toFixed(1)}%
                </p>
              </div>
            </div>

            {/* Job Completion */}
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Job Completion</p>
                <p className="font-semibold text-gray-900">
                  {(metrics.jobCompletionRate * 100).toFixed(0)}%
                </p>
              </div>
            </div>

            {/* Active Users */}
            <div className="flex items-center gap-2">
              <Users size={16} className="text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Active This Week</p>
                <p className="font-semibold text-gray-900">
                  {metrics.activeUsersThisWeek?.toLocaleString() || 0}
                </p>
              </div>
            </div>

            {/* Growth indicator */}
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-green-500" />
              <div>
                <p className="text-xs text-gray-500">Monthly Active</p>
                <p className="font-semibold text-gray-900">
                  {metrics.activeUsersThisMonth?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Commitment Statement */}
      {showCommitment && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 italic">
            "We don't just build the rules — we follow them. If we're not earning your trust, you'll see it in our score."
          </p>
          <p className="text-xs text-gray-500 mt-2">
            — UniLedge Platform Commitment
          </p>
        </div>
      )}
    </div>
  );
};

// Compact inline version for headers/footers
export const PlatformTrustBadge = ({ className = '' }) => {
  const { data } = useQuery(GET_PLATFORM_ACCOUNTABILITY, {
    fetchPolicy: 'cache-first'
  });

  const score = data?.platformAccountability?.platformTrustScore || 0;
  const colors = getScoreColor(score);

  return (
    <div className={`inline-flex items-center gap-1.5 ${colors.bg} ${colors.border} border rounded-full px-3 py-1 ${className}`}>
      <Shield size={14} className={colors.text} />
      <span className={`text-sm font-semibold ${colors.text}`}>
        {score.toFixed(1)}
      </span>
      <span className="text-xs text-gray-500">Platform Score</span>
    </div>
  );
};

// Mini version for navigation/footer
export const PlatformTrustMini = ({ className = '' }) => {
  const { data } = useQuery(GET_PLATFORM_ACCOUNTABILITY, {
    fetchPolicy: 'cache-first'
  });

  const score = data?.platformAccountability?.platformTrustScore || 0;
  const colors = getScoreColor(score);

  return (
    <div className={`inline-flex items-center gap-1 ${className}`} title="UniLedge Platform Trust Score">
      <Shield size={12} className={colors.text} />
      <span className={`text-xs font-medium ${colors.text}`}>
        {score.toFixed(1)}
      </span>
    </div>
  );
};

export default PlatformTrustScore;